/**
 * proposta-assinatura.service — the web app's boundary to the digital
 * signature backend.
 *
 * Integrador mutations (create / cancel / renovar / resend / signed-url) go
 * through the authenticated `assinatura-setup` Edge Function. Reads
 * (`getAssinaturaState`) hit PostgREST directly — U7 RLS already grants
 * `signatura_*` read access to anyone who can see the proposta.
 */

import { supabase } from '@/lib/supabase';
import type { SetupConfig } from '@fibonaccitec/assinatura/react';
import type {
  EventoTipo,
  Ordem,
  SessaoState,
  SignatarioState,
} from '@fibonaccitec/assinatura';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Maps an `assinatura-setup` error code to a pt-BR message for the UI. */
const ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'Sessão expirada. Faça login novamente.',
  forbidden: 'Você não tem permissão para gerenciar a assinatura desta proposta.',
  proposta_not_found: 'Proposta não encontrada.',
  sessao_not_found: 'Nenhuma assinatura ativa para esta proposta.',
  signatario_not_found: 'Signatário não encontrado.',
  signed_pdf_not_ready: 'O PDF assinado ainda não está disponível.',
  sessao_already_active: 'Já existe uma assinatura em andamento para esta proposta.',
  sessao_not_cancellable: 'Esta assinatura não pode mais ser cancelada.',
  sessao_not_renewable: 'Esta assinatura não pode ser renovada.',
  sessao_already_terminal: 'Esta assinatura já foi encerrada.',
  signatario_not_pending: 'Este signatário não está mais pendente.',
  signatario_not_correctable:
    'Este signatário não permite correção de e-mail (já assinou ou recusou).',
  resend_cap_reached: 'Limite de reenvios atingido para este signatário.',
  resend_cooldown: 'Aguarde alguns minutos antes de reenviar o convite.',
  correct_cooldown: 'Aguarde um momento antes de corrigir o e-mail novamente.',
  email_invalido: 'O e-mail informado é inválido.',
  email_duplicado: 'Este e-mail já pertence a outro signatário desta assinatura.',
  tenant_brand_incomplete:
    'Complete a identidade da empresa (nome, logo e cor) antes de enviar para assinatura.',
  tenant_subdomain_missing:
    'A empresa ainda não tem um subdomínio configurado para o portal de assinatura.',
  invalid_ordem: 'Ordem de assinatura inválida.',
  invalid_ttl: 'Prazo de assinatura inválido.',
  no_signatarios: 'Adicione ao menos um signatário.',
  no_areas: 'Posicione ao menos uma área de assinatura.',
  session_not_found: 'Nenhuma assinatura ativa para esta proposta.',
  already_signed: 'Esta pessoa já assinou.',
  not_in_turn: 'Ainda não é a vez desta pessoa.',
  path_invalid: 'Não foi possível guardar a assinatura. Tente novamente.',
  actor_name_blank: 'Não foi possível identificar quem entregou o iPad.',
  png_invalid: 'O desenho da assinatura não pôde ser lido. Desenhe de novo.',
  png_too_large: 'O desenho da assinatura ficou grande demais. Desenhe de novo.',
  deadlock: 'Não foi possível registrar a assinatura agora. Tente de novo.',
  cpf_invalid: 'Um dos CPFs informados é inválido.',
  email_dispatch_failed: 'Não foi possível enviar os e-mails de convite.',
  pdf_too_large:
    'O PDF da proposta está grande demais para enviar. Atualize a página e tente novamente.',
  pdf_missing: 'O PDF da proposta não chegou ao servidor. Tente enviar de novo.',
  network:
    'Não foi possível conectar ao servidor de assinatura. Atualize a página (Cmd+Shift+R), entre novamente e tente de novo.',
  internal: 'Erro inesperado. Tente novamente.',
};

/** A typed failure from the signature backend. `code` is stable; `message` is pt-BR. */
export class AssinaturaServiceError extends Error {
  constructor(public readonly code: string) {
    super(ERROR_MESSAGES[code] ?? ERROR_MESSAGES.internal);
    this.name = 'AssinaturaServiceError';
  }
}

// ---------------------------------------------------------------------------
// Edge Function transport
// ---------------------------------------------------------------------------

/** Invoke an `assinatura-setup` route; map a failure to `AssinaturaServiceError`. */
async function invokeSetup<T>(
  route: string,
  body: BodyInit | Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(`assinatura-setup/${route}`, {
    body,
  });
  if (error) {
    // supabase-js puts the failed Response on `error.context` — the EF body is
    // `{ error: <code> }`. See feedback_google_calendar_sync.
    // A CORS/network failure (isolate kill, gateway drop) has no readable body
    // and surfaces as FunctionsFetchError / "Failed to fetch".
    let code = 'internal';
    const message = error instanceof Error ? error.message : String(error);
    if (/failed to (send|fetch)|networkerror|load failed/i.test(message)) {
      code = 'network';
    }
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === 'function') {
      try {
        const parsed = (await context.json()) as { error?: string; code?: string };
        if (parsed?.error) code = parsed.error;
        else if (parsed?.code && /too.?large|payload/i.test(parsed.code)) {
          code = 'pdf_too_large';
        }
      } catch {
        /* keep the mapped code */
      }
    }
    throw new AssinaturaServiceError(code);
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface CreateSessaoInput {
  propostaId: string;
  /** Human label for the invite email, e.g. "Proposta 0042". */
  propostaLabel: string;
  /** The committed setup from the package's `SignatureSetupModal`. */
  config: SetupConfig;
  /** The frozen original PDF (R6). */
  pdfBytes: Uint8Array;
}

export interface CreateSessaoResult {
  sessaoId: string;
  expiresAt: string;
  emailsSent: number;
  emailsFailed: number[];
}

/** F1 — open a signature session for a proposta. */
export async function createSessao(
  input: CreateSessaoInput,
): Promise<CreateSessaoResult> {
  // Two-step: (1) mint a signed Storage URL, (2) PUT the PDF from the browser
  // straight to the bucket, (3) create-sessao with metadata only. Pushing the
  // PDF through the Edge Function (JSON+base64 or multipart) hung create-sessao
  // on 11-page propostas — the gateway never returned CORS headers.
  const prepared = await invokeSetup<{
    sessaoId: string;
    path: string;
    token: string;
  }>('prepare-upload', { propostaId: input.propostaId });

  const pdfCopy = new Uint8Array(input.pdfBytes);
  const blob = new Blob([pdfCopy.buffer as ArrayBuffer], { type: 'application/pdf' });
  // `signatura` is not in generated Storage types — signed upload token is the
  // authorization; the bucket has no client policies.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: uploadError } = await (supabase as any).storage
    .from('signatura')
    .uploadToSignedUrl(prepared.path, prepared.token, blob, {
      contentType: 'application/pdf',
    });
  if (uploadError) {
    console.error('signatura original PDF upload failed:', uploadError);
    throw new AssinaturaServiceError('internal');
  }

  return invokeSetup<CreateSessaoResult>('create-sessao', {
    propostaId: input.propostaId,
    propostaLabel: input.propostaLabel,
    ordem: input.config.ordem,
    ttlDias: input.config.ttlDias,
    dataFechamento: input.config.dataFechamento,
    criarProjeto: input.config.criarProjeto,
    precisaServico: input.config.precisaServico,
    signatarios: input.config.signatarios,
    areas: input.config.areas,
    sessaoId: prepared.sessaoId,
  });
}

/** Cancel an open signature session. */
export function cancelSessao(propostaId: string): Promise<{ cancelNotices: number }> {
  return invokeSetup('cancel', { propostaId });
}

/** Renew an expired session — resets the TTL, re-invites pending signatários. */
export function renovarSessao(
  propostaId: string,
  ttlDias: number,
  propostaLabel: string,
): Promise<{ expiresAt: string; emailsSent: number; emailsFailed: number[] }> {
  return invokeSetup('renovar', { propostaId, ttlDias, propostaLabel });
}

/** Resend one signatário's invitation (R37 cooldown + cap enforced server-side). */
export function resendInvite(
  propostaId: string,
  signatarioIndice: number,
  propostaLabel: string,
): Promise<{ resendCount: number }> {
  return invokeSetup('resend', { propostaId, signatarioIndice, propostaLabel });
}

/**
 * Correct a signatário's address (when pendente / email_undeliverable) and
 * re-send the invitation to the new address. Server resets the signatário to
 * pendente and re-arms the link token.
 */
export function correctEmailAndResend(
  propostaId: string,
  signatarioIndice: number,
  newEmail: string,
  propostaLabel: string,
): Promise<{ ok: true }> {
  return invokeSetup('correct-email', {
    propostaId,
    signatarioIndice,
    newEmail,
    propostaLabel,
  });
}

/** Mint a 5-minute signed URL for the final stamped PDF. */
export async function signedPdfUrl(propostaId: string): Promise<string> {
  const result = await invokeSetup<{ url: string }>('signed-url', { propostaId });
  return result.url;
}

// ---------------------------------------------------------------------------
// Reads (PostgREST — RLS mirrors proposta visibility)
// ---------------------------------------------------------------------------

export interface AssinaturaSessao {
  id: string;
  state: SessaoState;
  ordem: Ordem;
  ttl_dias: number;
  expires_at: string | null;
  data_fechamento: string | null;
  criar_projeto: boolean;
  precisa_servico: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface AssinaturaSignatario {
  indice: number;
  nome: string;
  email: string;
  cpf_mascarado: string | null;
  state: SignatarioState;
  signed_at: string | null;
  decline_reason: string | null;
}

export interface AssinaturaEvento {
  id: string;
  /**
   * `signatura_evento.tipo` is free text in the DB — new event types (e.g.
   * email_suppressed, email_corrected) ship without a migration on the
   * append-only table, so this is open-ended beyond the known `EventoTipo`.
   */
  tipo: EventoTipo | string;
  signatario_indice: number | null;
  created_at: string;
  payload: Record<string, unknown>;
}

export interface AssinaturaState {
  sessao: AssinaturaSessao;
  signatarios: AssinaturaSignatario[];
  eventos: AssinaturaEvento[];
}

/**
 * Load the current signature state of a proposta, or `null` when no session
 * has ever been opened (the AssinaturaTab `rascunho` state).
 */
export async function getAssinaturaState(
  propostaId: string,
): Promise<AssinaturaState | null> {
  // The `signatura_*` tables + `v_signatura_evento_summary` view are not in the
  // generated Supabase types yet — cast the client at this boundary. Caller-
  // facing type safety is restored by the `Assinatura*` interfaces above.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any;

  const { data: sessao, error: sessaoError } = await sb
    .from('signatura_sessao')
    .select(
      'id, state, ordem, ttl_dias, expires_at, data_fechamento, criar_projeto, precisa_servico, created_at, updated_at',
    )
    .eq('entidade_tipo', 'proposta')
    .eq('entidade_id', propostaId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (sessaoError) throw sessaoError;
  if (!sessao) return null;

  const sessaoId = (sessao as AssinaturaSessao).id;

  const [signatariosRes, eventosRes] = await Promise.all([
    sb
      .from('signatura_signatario')
      .select('indice, nome, email, cpf_mascarado, state, signed_at, decline_reason')
      .eq('sessao_id', sessaoId)
      .is('deleted_at', null)
      .order('indice'),
    sb
      .from('v_signatura_evento_summary')
      .select('id, tipo, signatario_indice, created_at, payload')
      .eq('sessao_id', sessaoId)
      .order('created_at', { ascending: false }),
  ]);
  if (signatariosRes.error) throw signatariosRes.error;
  if (eventosRes.error) throw eventosRes.error;

  return {
    sessao: sessao as AssinaturaSessao,
    signatarios: (signatariosRes.data ?? []) as AssinaturaSignatario[],
    eventos: (eventosRes.data ?? []) as AssinaturaEvento[],
  };
}

// ---------------------------------------------------------------------------
// In-person handoff (seller iPad)
// ---------------------------------------------------------------------------

/** One signature rectangle placed for the person holding the iPad. */
export interface PresencialArea {
  id: string;
  pageIndex: number;
  xPt: number;
  yPt: number;
  widthPt: number;
  heightPt: number;
  pageWidthPt: number;
  pageHeightPt: number;
  signedAt: string | null;
}

/**
 * Areas for one signatory, through PostgREST.
 * The read route returns a URL only — it does not return these rows.
 */
export async function loadPresencialAreas(
  sessaoId: string,
  signatarioIndice: number,
): Promise<PresencialArea[]> {
  const { data: signer, error: signerError } = await supabase
    .from('signatura_signatario')
    .select('id')
    .eq('sessao_id', sessaoId)
    .eq('indice', signatarioIndice)
    .is('deleted_at', null)
    .maybeSingle();
  if (signerError) throw signerError;
  if (!signer) return [];

  const { data: areas, error: areasError } = await supabase
    .from('signatura_areas')
    .select(
      'id, page_index, x_pt, y_pt, width_pt, height_pt, page_width_pt, page_height_pt, signed_at',
    )
    .eq('signatario_id', signer.id)
    .order('page_index', { ascending: true })
    .order('id', { ascending: true });
  if (areasError) throw areasError;

  return (areas ?? []).map((area) => ({
    id: area.id,
    pageIndex: area.page_index,
    xPt: area.x_pt,
    yPt: area.y_pt,
    widthPt: area.width_pt,
    heightPt: area.height_pt,
    pageWidthPt: area.page_width_pt,
    pageHeightPt: area.page_height_pt,
    signedAt: area.signed_at,
  }));
}

/** Mint a short-lived URL for the frozen original. The body is `{ ok, url }` only. */
export async function readPresencialOriginalUrl(sessaoId: string): Promise<string> {
  const result = await invokeSetup<{ ok: true; url: string }>('presencial/original', {
    sessaoId,
  });
  return result.url;
}

/**
 * Record the adopted PNG for every area of this signatory.
 * `signaturePng` is raw base64, without a data-URL prefix.
 */
export function confirmPresencialSignature(
  sessaoId: string,
  signatarioIndice: number,
  signaturePng: string,
): Promise<{ ok: true }> {
  return invokeSetup('presencial/confirm', {
    sessaoId,
    signatarioIndice,
    signaturePng,
  });
}

/** Trade name shown on the locked screen. A miss leaves the subtitle to carry the person. */
export async function loadPresencialBrandName(tenantId: string | null): Promise<string> {
  if (!tenantId) return '';
  const { data, error } = await supabase
    .from('tenant')
    .select('nome, nome_fantasia')
    .eq('id', tenantId)
    .maybeSingle();
  if (error || !data) return '';
  const trade = data.nome_fantasia?.trim();
  if (trade) return trade;
  return data.nome?.trim() ?? '';
}
